import React, { useState, useEffect, useMemo } from "react";
import {
  Button,
  Card,
  CardContent,
  Typography,
  Container,
  Grid,
  Stack,
  Box,
  Divider,
  CircularProgress,
  Alert,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemIcon,
  Avatar,
  Chip,
  Paper,
  Tabs,
  Tab,
  Snackbar,
  Autocomplete,
  Badge,
  LinearProgress,
  FormControlLabel,
  Menu,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import PeopleIcon from "@mui/icons-material/People";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import WorkIcon from "@mui/icons-material/Work";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import EmailIcon from "@mui/icons-material/Email";
import FilterListIcon from "@mui/icons-material/FilterList";
import DashboardIcon from "@mui/icons-material/Dashboard";
import GroupsIcon from "@mui/icons-material/Groups";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { getToken, base_url } from "../../config/config";

// Function to generate avatar color based on name
const stringToColor = (string) => {
  let hash = 0;
  for (let i = 0; i < string.length; i++) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }
  let color = "#";
  for (let i = 0; i < 3; i++) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  return color;
};

// Function to generate avatar props
const stringAvatar = (name) => {
  return {
    sx: {
      bgcolor: stringToColor(name),
    },
    children: name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase(),
  };
};

const EmployeeView = () => {
  // State variables for employees and groups
  const [employees, setEmployees] = useState([]);
  const [groups, setGroups] = useState([]);

  // Loading and error states
  const [loadingEmployees, setLoadingEmployees] = useState(true);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [errorEmployees, setErrorEmployees] = useState(null);
  const [errorGroups, setErrorGroups] = useState(null);

  // Employment status dialog state
  const [openEmploymentStatusDialog, setOpenEmploymentStatusDialog] =
    useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employmentFormData, setEmploymentFormData] = useState({
    employmentStartDate: "",
    employmentEndDate: "",
    isFormerEmployee: false,
  });

  // Search and filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState("all");
  const [selectedShift, setSelectedShift] = useState("all");
  const [departments, setDepartments] = useState([]);

  // View state
  const [tabValue, setTabValue] = useState(0);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Group creation dialog state
  const [openGroupDialog, setOpenGroupDialog] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [selectedEmployees, setSelectedEmployees] = useState([]);

  // Add Employee Dialog State
  const [openAddEmployeeDialog, setOpenAddEmployeeDialog] = useState(false);
  const [newEmployee, setNewEmployee] = useState({
    name: "",
    email: "",
    position: "",
    shift: "",
    department: "",
    skills: [],
    employmentStartDate: "",
    employmentEndDate: "",
    isFormerEmployee: false,
  });
  const [shifts, setShifts] = useState([]);
  const [skillInput, setSkillInput] = useState("");

  // Navigation hook
  const navigate = useNavigate();

  // Base API URL
  const API_BASE_URL = `${base_url}/v1`;

  // Fetch employees from API
  const fetchEmployees = async () => {
    setLoadingEmployees(true);
    setErrorEmployees(null);
    try {
      const response = await axios.get(`${API_BASE_URL}/getAllEmployees`);
      const fetchedEmployees = response.data.map((employee) => ({
        ...employee,
        _id: String(employee._id),
        skills: Array.isArray(employee.skills) ? employee.skills : [],
      }));
      setEmployees(fetchedEmployees);

      // Extract unique departments
      const uniqueDepartments = [
        ...new Set(
          fetchedEmployees
            .map((emp) => emp.department)
            .filter((dept) => dept && dept !== ""),
        ),
      ];
      setDepartments(uniqueDepartments);

      setLoadingEmployees(false);
    } catch (error) {
      console.error("Error fetching employees:", error);
      setErrorEmployees("Failed to load employees. Please try again later.");
      setLoadingEmployees(false);
    }
  };

  // Toggle employee active status
  const handleToggleEmployeeStatus = async (employeeId) => {
    try {
      const response = await axios.put(
        `${API_BASE_URL}/toggleEmployeeStatus/${employeeId}`,
      );

      if (response.status === 200) {
        // Update the employee status in the local state
        setEmployees((prevEmployees) =>
          prevEmployees.map((emp) =>
            emp._id === employeeId ? { ...emp, isActive: !emp.isActive } : emp,
          ),
        );

        // Show success message
        setSnackbar({
          open: true,
          message: `Employee ${response.data.employee.isActive ? "activated" : "deactivated"} successfully`,
          severity: "success",
        });
      }
    } catch (error) {
      console.error("Error toggling employee status:", error);
      setSnackbar({
        open: true,
        message: "Failed to update employee status",
        severity: "error",
      });
    }
  };

  // Update employee employment status
  const handleUpdateEmploymentStatus = async (employeeId, data) => {
    try {
      const response = await axios.put(
        `${API_BASE_URL}/updateEmploymentStatus/${employeeId}`,
        data,
      );

      if (response.status === 200) {
        // Update the employee in the local state
        setEmployees((prevEmployees) =>
          prevEmployees.map((emp) =>
            emp._id === employeeId
              ? { ...emp, ...response.data.employee }
              : emp,
          ),
        );

        // Show success message
        setSnackbar({
          open: true,
          message: "Employment status updated successfully",
          severity: "success",
        });
      }
    } catch (error) {
      console.error("Error updating employment status:", error);
      setSnackbar({
        open: true,
        message: "Failed to update employment status",
        severity: "error",
      });
    }
  };

  // Fetch groups from API
  const fetchGroups = async () => {
    setLoadingGroups(true);
    setErrorGroups(null);
    try {
      const response = await axios.get(`${API_BASE_URL}/getAllGroups/`);
      const fetchedGroups = response.data.map((group) => ({
        ...group,
        _id: String(group._id),
        employees: Array.isArray(group.employees)
          ? group.employees.map((emp) =>
              typeof emp === "object" ? String(emp._id) : String(emp),
            )
          : [],
      }));
      setGroups(fetchedGroups);
      setLoadingGroups(false);
    } catch (error) {
      console.error("Error fetching groups:", error);
      setErrorGroups("Failed to load groups. Please try again later.");
      setLoadingGroups(false);
    }
  };

  // Fetch Shifts
  const fetchShifts = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/getshifts`);
      setShifts(response.data);
    } catch (error) {
      console.error("Error fetching shifts:", error);
    }
  };

  // useEffect to fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      await fetchEmployees();
      await fetchGroups();
      await fetchShifts();
    };
    fetchData();
  }, []);

  // Handle search input change
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Filter handlers
  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleDepartmentChange = (event) => {
    setSelectedDepartment(event.target.value);
  };

  const handleShiftChange = (event) => {
    setSelectedShift(event.target.value);
  };

  // Group dialog handlers
  const handleOpenGroupDialog = () => {
    setOpenGroupDialog(true);
  };

  const handleCloseGroupDialog = () => {
    setOpenGroupDialog(false);
    setGroupName("");
    setSelectedEmployees([]);
  };

  // Handle employee selection in group creation
  const handleEmployeeToggle = (employeeId) => {
    const currentIndex = selectedEmployees.indexOf(employeeId);
    const newSelected = [...selectedEmployees];

    if (currentIndex === -1) {
      newSelected.push(employeeId);
    } else {
      newSelected.splice(currentIndex, 1);
    }

    setSelectedEmployees(newSelected);
  };

  // Employment status dialog handlers
  const handleOpenEmploymentStatusDialog = (employee) => {
    setSelectedEmployee(employee);
    setEmploymentFormData({
      employmentStartDate: employee.employmentStartDate
        ? new Date(employee.employmentStartDate).toISOString().split("T")[0]
        : "",
      employmentEndDate: employee.employmentEndDate
        ? new Date(employee.employmentEndDate).toISOString().split("T")[0]
        : "",
      isFormerEmployee: employee.isFormerEmployee || false,
    });
    setOpenEmploymentStatusDialog(true);
  };

  const handleCloseEmploymentStatusDialog = () => {
    setOpenEmploymentStatusDialog(false);
    setSelectedEmployee(null);
  };

  const handleEmploymentFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEmploymentFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmitEmploymentStatus = () => {
    if (!selectedEmployee) return;

    handleUpdateEmploymentStatus(selectedEmployee._id, employmentFormData);
    handleCloseEmploymentStatusDialog();
  };

  // Handle group creation
  const handleCreateGroup = async () => {
    if (!groupName.trim()) {
      setSnackbar({
        open: true,
        message: "Group name cannot be empty",
        severity: "error",
      });
      return;
    }

    try {
      const payload = {
        name: groupName.trim(),
        employees: selectedEmployees,
      };
      const response = await axios.post(`${API_BASE_URL}/createGroup`, payload);
      const newGroup = {
        ...response.data,
        _id: String(response.data._id),
        employees: Array.isArray(response.data.employees)
          ? response.data.employees.map((id) => String(id))
          : [],
      };
      setGroups([...groups, newGroup]);
      handleCloseGroupDialog();
      setSnackbar({
        open: true,
        message: "Group created successfully",
        severity: "success",
      });
    } catch (error) {
      console.error("Error creating group:", error);
      setSnackbar({
        open: true,
        message: "Failed to create group",
        severity: "error",
      });
    }
  };

  // Handle group deletion
  const handleDeleteGroup = async (groupId) => {
    if (window.confirm("Are you sure you want to delete this group?")) {
      try {
        await axios.delete(`${API_BASE_URL}/deleteGroup/${groupId}`);
        setGroups(groups.filter((group) => group._id !== groupId));
        setSnackbar({
          open: true,
          message: "Group deleted successfully",
          severity: "success",
        });
      } catch (error) {
        console.error("Error deleting group:", error);
        setSnackbar({
          open: true,
          message: "Failed to delete group",
          severity: "error",
        });
      }
    }
  };

  // Add Employee Dialog Handlers
  const handleOpenAddEmployeeDialog = () => {
    setOpenAddEmployeeDialog(true);
  };

  const handleCloseAddEmployeeDialog = () => {
    setOpenAddEmployeeDialog(false);
    // Reset form
    setNewEmployee({
      name: "",
      email: "",
      position: "",
      shift: "",
      department: "",
      skills: [],
      employmentStartDate: "",
      employmentEndDate: "",
      isFormerEmployee: false,
    });
    setSkillInput("");
  };

  const handleNewEmployeeChange = (e) => {
    const { name, value } = e.target;
    setNewEmployee((prev) => ({ ...prev, [name]: value }));
  };

  const handleSkillDelete = (skillToDelete) => {
    setNewEmployee((prev) => ({
      ...prev,
      skills: prev.skills.filter((skill) => skill !== skillToDelete),
    }));
  };

  const handleSkillAdd = () => {
    if (skillInput.trim() && !newEmployee.skills.includes(skillInput.trim())) {
      setNewEmployee((prev) => ({
        ...prev,
        skills: [...prev.skills, skillInput.trim()],
      }));
      setSkillInput("");
    }
  };

  const handleAddEmployeeSubmit = async () => {
    // Basic validation
    if (
      !newEmployee.name ||
      !newEmployee.email ||
      !newEmployee.position ||
      !newEmployee.shift
    ) {
      setSnackbar({
        open: true,
        message: "Please fill out all required employee fields",
        severity: "error",
      });
      return;
    }

    try {
      await axios.post(`${API_BASE_URL}/createEmployee`, newEmployee);
      handleCloseAddEmployeeDialog();
      // Refresh the employee list
      fetchEmployees();
      setSnackbar({
        open: true,
        message: "Employee added successfully",
        severity: "success",
      });
    } catch (error) {
      console.error("Error adding employee:", error);
      setSnackbar({
        open: true,
        message: "Failed to add employee",
        severity: "error",
      });
    }
  };

  // Close snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Filtered employees based on search query and filters
  const filteredEmployees = useMemo(() => {
    return employees.filter((employee) => {
      const matchesSearch = employee.name
        .toLowerCase()
        .includes(searchQuery.toLowerCase());
      const matchesDepartment =
        selectedDepartment === "all" ||
        (employee.department && employee.department === selectedDepartment);
      const matchesShift =
        selectedShift === "all" ||
        (employee.shift && employee.shift === selectedShift);

      return matchesSearch && matchesDepartment && matchesShift;
    });
  }, [employees, searchQuery, selectedDepartment, selectedShift]);

  // Create an employee map for efficient lookups
  const employeeMap = useMemo(() => {
    const map = {};
    employees.forEach((employee) => {
      map[employee._id] = employee;
    });
    return map;
  }, [employees]);

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
      <Container maxWidth="xl" sx={{ paddingY: 4 }}>
        {/* Modern App Header with Dashboard-like Stats */}
        <Paper
          elevation={3}
          sx={{
            padding: 3,
            marginBottom: 4,
            borderRadius: 2,
            background: "linear-gradient(135deg, #2196f3 0%, #1976d2 100%)",
            color: "white",
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <Box display="flex" alignItems="center" gap={2}>
                <DashboardIcon sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h4" component="h1" fontWeight="500">
                    Mitarbeiterverzeichnis
                  </Typography>
                  <Typography variant="subtitle1">
                    Verwalten Sie Ihr Team und Gruppen mit Leichtigkeit
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box
                    sx={{
                      background: "rgba(255,255,255,0.1)",
                      borderRadius: 1,
                      padding: 1.5,
                      backdropFilter: "blur(10px)",
                    }}
                  >
                    <Typography variant="overline">
                      Mitarbeiter Gesamt
                    </Typography>
                    <Typography variant="h4">{employees.length}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box
                    sx={{
                      background: "rgba(255,255,255,0.1)",
                      borderRadius: 1,
                      padding: 1.5,
                      backdropFilter: "blur(10px)",
                    }}
                  >
                    <Typography variant="overline">Aktive Gruppen</Typography>
                    <Typography variant="h4">{groups.length}</Typography>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Paper>

        {/* Tab Navigation */}
        <Box sx={{ borderBottom: 1, borderColor: "divider", marginBottom: 3 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="fullWidth"
            textColor="primary"
            indicatorColor="primary"
          >
            <Tab
              label="Mitarbeiter"
              icon={<PeopleIcon />}
              iconPosition="start"
            />
            <Tab label="Gruppen" icon={<GroupsIcon />} iconPosition="start" />
          </Tabs>
        </Box>

        {/* Search and Action Bar */}
        <Paper
          elevation={1}
          sx={{ padding: 2, marginBottom: 3, borderRadius: 2 }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Mitarbeiter nach Namen suchen..."
                value={searchQuery}
                onChange={handleSearchChange}
                variant="outlined"
                size="small"
                InputProps={{
                  startAdornment: (
                    <SearchIcon sx={{ color: "text.secondary", mr: 1 }} />
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Stack
                direction="row"
                spacing={1}
                justifyContent={{ xs: "center", md: "flex-end" }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<PersonAddIcon />}
                  onClick={handleOpenAddEmployeeDialog}
                >
                  Mitarbeiter hinzufügen
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<GroupAddIcon />}
                  onClick={handleOpenGroupDialog}
                >
                  Gruppe erstellen
                </Button>
                <Tooltip title="Daten aktualisieren">
                  <IconButton
                    color="primary"
                    onClick={() => {
                      fetchEmployees();
                      fetchGroups();
                      fetchShifts();
                    }}
                  >
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Filtern">
                  <IconButton
                    color={
                      selectedDepartment !== "all" || selectedShift !== "all"
                        ? "primary"
                        : "default"
                    }
                    onClick={handleFilterClick}
                  >
                    <Badge
                      color="secondary"
                      variant="dot"
                      invisible={
                        selectedDepartment === "all" && selectedShift === "all"
                      }
                    >
                      <FilterListIcon />
                    </Badge>
                  </IconButton>
                </Tooltip>
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        {/* Filter Menu */}
        <Menu
          anchorEl={filterAnchorEl}
          open={Boolean(filterAnchorEl)}
          onClose={handleFilterClose}
        >
          <MenuItem>
            <FormControl fullWidth sx={{ minWidth: 200 }}>
              <InputLabel>Abteilung</InputLabel>
              <Select
                value={selectedDepartment}
                onChange={handleDepartmentChange}
                label="Abteilung"
                size="small"
              >
                <MenuItem value="all">Alle Abteilungen</MenuItem>
                {departments.map((dept) => (
                  <MenuItem key={dept} value={dept}>
                    {dept}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </MenuItem>
          <MenuItem>
            <FormControl fullWidth>
              <InputLabel>Schicht</InputLabel>
              <Select
                value={selectedShift}
                onChange={handleShiftChange}
                label="Schicht"
                size="small"
              >
                <MenuItem value="all">Alle Schichten</MenuItem>
                {shifts.map((shift) => (
                  <MenuItem key={shift._id} value={shift._id}>
                    {shift.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </MenuItem>
          <Box sx={{ display: "flex", justifyContent: "flex-end", padding: 1 }}>
            <Button
              size="small"
              onClick={() => {
                setSelectedDepartment("all");
                setSelectedShift("all");
              }}
            >
              Filter zurücksetzen
            </Button>
          </Box>
        </Menu>

        {/* Loading State */}
        {(loadingEmployees || loadingGroups) && (
          <Box sx={{ width: "100%", marginBottom: 2 }}>
            <LinearProgress />
          </Box>
        )}

        {/* Error Messages */}
        {errorEmployees && (
          <Alert severity="error" sx={{ marginBottom: 2 }}>
            {errorEmployees}
          </Alert>
        )}
        {errorGroups && (
          <Alert severity="error" sx={{ marginBottom: 2 }}>
            {errorGroups}
          </Alert>
        )}

        {/* Tab Panels */}
        <TabPanel value={tabValue} index={0}>
          {/* Employees Tab */}
          {filteredEmployees.length > 0 ? (
            <Grid container spacing={3}>
              {filteredEmployees.map((employee) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={employee._id}>
                  <Card
                    elevation={2}
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: 2,
                      transition: "transform 0.2s, box-shadow 0.2s",
                      "&:hover": {
                        transform: "translateY(-5px)",
                        boxShadow: 6,
                      },
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1, padding: 3 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          marginBottom: 2,
                        }}
                      >
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Avatar {...stringAvatar(employee.name)} />
                          <Box>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 500,
                                cursor: "pointer",
                                "&:hover": { textDecoration: "underline" },
                              }}
                              onClick={() =>
                                navigate(`/employee/update/${employee._id}`)
                              }
                            >
                              {employee.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {employee.position || "Keine Position"}
                            </Typography>
                          </Box>
                        </Stack>
                        <Box sx={{ display: "flex" }}>
                          <IconButton
                            size="small"
                            onClick={() =>
                              navigate(`/employee/update/${employee._id}`)
                            }
                            sx={{ mr: 1 }}
                          >
                            <EditIcon />
                          </IconButton>
                          <Tooltip
                            title={
                              employee.isActive === false
                                ? "Für Vorlagen aktivieren"
                                : "Aus Vorlagen entfernen"
                            }
                          >
                            <IconButton
                              size="small"
                              color={
                                employee.isActive === false
                                  ? "error"
                                  : "success"
                              }
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleEmployeeStatus(employee._id);
                              }}
                              sx={{ mr: 1 }}
                            >
                              {employee.isActive === false ? (
                                <VisibilityOffIcon />
                              ) : (
                                <VisibilityIcon />
                              )}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Beschäftigungsdaten verwalten">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenEmploymentStatusDialog(employee);
                              }}
                            >
                              <AccessTimeIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>

                      <Stack spacing={1.5} sx={{ marginBottom: 2 }}>
                        {employee.department && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <WorkIcon fontSize="small" color="action" />
                            <Typography variant="body2">
                              {employee.department}
                            </Typography>
                          </Box>
                        )}
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <EmailIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            {employee.email || "Keine E-Mail"}
                          </Typography>
                        </Box>
                        {employee.shift && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <AccessTimeIcon fontSize="small" color="action" />
                            <Typography variant="body2">
                              {shifts.find((s) => s._id === employee.shift)
                                ?.name || "Unbekannte Schicht"}
                            </Typography>
                          </Box>
                        )}

                        {/* Employment Status Display */}
                        <Box>
                          {employee.isFormerEmployee ? (
                            <Chip
                              label="Ehemaliger Mitarbeiter"
                              color="error"
                              size="small"
                              variant="outlined"
                              icon={<VisibilityOffIcon />}
                            />
                          ) : (
                            <Box>
                              {employee.employmentStartDate && (
                                <Chip
                                  label={`Beginn: ${new Date(employee.employmentStartDate).toLocaleDateString()}`}
                                  color="primary"
                                  size="small"
                                  variant="outlined"
                                  sx={{ mr: 1, mb: 1 }}
                                />
                              )}
                              {employee.employmentEndDate && (
                                <Chip
                                  label={`Ende: ${new Date(employee.employmentEndDate).toLocaleDateString()}`}
                                  color={
                                    new Date(employee.employmentEndDate) <
                                    new Date()
                                      ? "error"
                                      : "warning"
                                  }
                                  size="small"
                                  variant="outlined"
                                  sx={{ mb: 1 }}
                                />
                              )}
                            </Box>
                          )}
                          {!employee.isActive && !employee.isFormerEmployee && (
                            <Chip
                              label="Inaktiv"
                              color="default"
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      </Stack>

                      <Divider sx={{ marginY: 2 }} />

                      <Typography variant="subtitle2" gutterBottom>
                        Fähigkeiten ({employee.skills.length})
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                        {employee.skills.length > 0 ? (
                          employee.skills.map((skill, index) => (
                            <Chip
                              key={index}
                              label={skill}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          ))
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Keine Fähigkeiten aufgelistet
                          </Typography>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                padding: 5,
                textAlign: "center",
              }}
            >
              <PeopleIcon
                sx={{ fontSize: 60, color: "text.secondary", mb: 2 }}
              />
              <Typography variant="h6" color="text.secondary">
                Keine Mitarbeiter gefunden
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {searchQuery
                  ? "Versuchen Sie andere Suchkriterien oder Filter"
                  : "Fügen Sie Ihren ersten Mitarbeiter hinzu, um zu beginnen"}
              </Typography>
              {!searchQuery && (
                <Button
                  variant="contained"
                  startIcon={<PersonAddIcon />}
                  onClick={handleOpenAddEmployeeDialog}
                  sx={{ mt: 3 }}
                >
                  Mitarbeiter hinzufügen
                </Button>
              )}
            </Box>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {/* Groups Tab */}
          {groups.length > 0 ? (
            <Grid container spacing={3}>
              {groups.map((group) => (
                <Grid item xs={12} sm={6} md={4} key={group._id}>
                  <Card
                    elevation={2}
                    sx={{
                      borderRadius: 2,
                      height: "100%",
                      transition: "transform 0.2s, box-shadow 0.2s",
                      "&:hover": {
                        transform: "translateY(-5px)",
                        boxShadow: 6,
                      },
                    }}
                  >
                    <CardContent>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: 2,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                          }}
                        >
                          <Avatar sx={{ bgcolor: "secondary.main" }}>
                            <GroupsIcon />
                          </Avatar>
                          <Typography variant="h6" color="secondary.main">
                            {group.name}
                          </Typography>
                        </Box>
                        <Tooltip title="Delete Group">
                          <IconButton
                            color="error"
                            onClick={() => handleDeleteGroup(group._id)}
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>

                      <Divider sx={{ marginY: 2 }} />

                      <Box>
                        <Typography variant="subtitle2" gutterBottom>
                          Members ({group.employees.length})
                        </Typography>

                        {group.employees.length > 0 ? (
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 1,
                            }}
                          >
                            {group.employees
                              .slice(0, 5)
                              .map((employeeId, index) => {
                                const id =
                                  typeof employeeId === "object"
                                    ? employeeId._id
                                    : employeeId;
                                const emp = employeeMap[id];
                                return emp ? (
                                  <Box
                                    key={index}
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1.5,
                                      padding: 1,
                                      borderRadius: 1,
                                      "&:hover": { bgcolor: "action.hover" },
                                    }}
                                  >
                                    <Avatar
                                      {...stringAvatar(emp.name)}
                                      sx={{ width: 32, height: 32 }}
                                    />
                                    <Typography variant="body2">
                                      {emp.name}
                                    </Typography>
                                  </Box>
                                ) : null;
                              })}

                            {group.employees.length > 5 && (
                              <Box sx={{ textAlign: "center", mt: 1 }}>
                                <Chip
                                  label={`+ ${group.employees.length - 5} more`}
                                  size="small"
                                  color="secondary"
                                  variant="outlined"
                                />
                              </Box>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No members in this group
                          </Typography>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                padding: 5,
                textAlign: "center",
              }}
            >
              <GroupsIcon
                sx={{ fontSize: 60, color: "text.secondary", mb: 2 }}
              />
              <Typography variant="h6" color="text.secondary">
                No groups found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Create your first group to organize your team
              </Typography>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<GroupAddIcon />}
                onClick={handleOpenGroupDialog}
                sx={{ mt: 3 }}
              >
                Create Group
              </Button>
            </Box>
          )}
        </TabPanel>

        {/* Group Creation Dialog */}
        <Dialog
          open={openGroupDialog}
          onClose={handleCloseGroupDialog}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <GroupAddIcon color="secondary" />
              Create New Group
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box component="form" noValidate sx={{ mt: 2 }}>
              <TextField
                label="Group Name"
                variant="outlined"
                fullWidth
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                required
                margin="normal"
              />

              {employees.length > 0 ? (
                <FormControl fullWidth margin="normal">
                  <Autocomplete
                    multiple
                    id="employee-selector"
                    options={employees}
                    getOptionLabel={(option) => option.name}
                    value={employees.filter((emp) =>
                      selectedEmployees.includes(emp._id),
                    )}
                    onChange={(event, newValue) => {
                      setSelectedEmployees(newValue.map((emp) => emp._id));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Employees"
                        placeholder="Search employees..."
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                          }}
                        >
                          <Avatar
                            {...stringAvatar(option.name)}
                            sx={{ width: 24, height: 24 }}
                          />
                          <Typography variant="body2">{option.name}</Typography>
                          {option.position && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              ({option.position})
                            </Typography>
                          )}
                        </Box>
                      </li>
                    )}
                  />
                </FormControl>
              ) : (
                <Alert severity="info" sx={{ mt: 2 }}>
                  No employees available. Add employees first to create a group.
                </Alert>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseGroupDialog}>Cancel</Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleCreateGroup}
              disabled={!groupName.trim() || selectedEmployees.length === 0}
            >
              Create Group
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add Employee Dialog */}
        <Dialog
          open={openAddEmployeeDialog}
          onClose={handleCloseAddEmployeeDialog}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <PersonAddIcon color="primary" />
              Add New Employee
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box component="form" noValidate sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    label="Full Name"
                    name="name"
                    value={newEmployee.name}
                    onChange={handleNewEmployeeChange}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    label="Email"
                    name="email"
                    type="email"
                    value={newEmployee.email}
                    onChange={handleNewEmployeeChange}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    label="Position"
                    name="position"
                    value={newEmployee.position}
                    onChange={handleNewEmployeeChange}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Department"
                    name="department"
                    value={newEmployee.department}
                    onChange={handleNewEmployeeChange}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControl required fullWidth>
                    <InputLabel>Shift</InputLabel>
                    <Select
                      name="shift"
                      value={newEmployee.shift}
                      onChange={handleNewEmployeeChange}
                      label="Shift"
                    >
                      {shifts.map((shift) => (
                        <MenuItem key={shift._id} value={shift._id}>
                          {shift.name} ({shift.startTime} - {shift.endTime})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Employment Date Fields */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Employment Period
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Start Date"
                    name="employmentStartDate"
                    type="date"
                    value={newEmployee.employmentStartDate}
                    onChange={handleNewEmployeeChange}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    helperText="When the employee started"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="End Date"
                    name="employmentEndDate"
                    type="date"
                    value={newEmployee.employmentEndDate}
                    onChange={handleNewEmployeeChange}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    helperText="Leave empty if still employed"
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={newEmployee.isFormerEmployee}
                        onChange={(e) =>
                          setNewEmployee({
                            ...newEmployee,
                            isFormerEmployee: e.target.checked,
                            isActive: e.target.checked
                              ? false
                              : newEmployee.isActive,
                          })
                        }
                        name="isFormerEmployee"
                      />
                    }
                    label="Mark as former employee"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Skills
                  </Typography>
                  <Box
                    sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}
                  >
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Add skill..."
                      value={skillInput}
                      onChange={(e) => setSkillInput(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleSkillAdd();
                        }
                      }}
                    />
                    <Button
                      variant="outlined"
                      onClick={handleSkillAdd}
                      disabled={!skillInput.trim()}
                    >
                      Add
                    </Button>
                  </Box>
                  <Box
                    sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}
                  >
                    {newEmployee.skills.length > 0 ? (
                      newEmployee.skills.map((skill, index) => (
                        <Chip
                          key={index}
                          label={skill}
                          onDelete={() => handleSkillDelete(skill)}
                          color="primary"
                          size="small"
                        />
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No skills added yet
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAddEmployeeDialog}>Cancel</Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleAddEmployeeSubmit}
              disabled={
                !newEmployee.name ||
                !newEmployee.email ||
                !newEmployee.position ||
                !newEmployee.shift
              }
            >
              Add Employee
            </Button>
          </DialogActions>
        </Dialog>

        {/* Employment Status Dialog */}
        <Dialog
          open={openEmploymentStatusDialog}
          onClose={handleCloseEmploymentStatusDialog}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <AccessTimeIcon color="primary" />
              {selectedEmployee
                ? `Update Employment Status: ${selectedEmployee.name}`
                : "Update Employment Status"}
            </Box>
          </DialogTitle>
          <DialogContent>
            {selectedEmployee && (
              <Box component="form" noValidate sx={{ mt: 2 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Employment Start Date"
                      name="employmentStartDate"
                      type="date"
                      value={employmentFormData.employmentStartDate}
                      onChange={handleEmploymentFormChange}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      helperText="When employee started working"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Employment End Date"
                      name="employmentEndDate"
                      type="date"
                      value={employmentFormData.employmentEndDate}
                      onChange={handleEmploymentFormChange}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      helperText="Leave empty if still employed"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={employmentFormData.isFormerEmployee}
                          onChange={handleEmploymentFormChange}
                          name="isFormerEmployee"
                          color="primary"
                        />
                      }
                      label="Mark as former employee (will disable login and remove from templates)"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Alert severity="info">
                      Former employees won't be able to log in and won't appear
                      in template maker. Their data is preserved in the system
                      for record-keeping purposes.
                    </Alert>
                  </Grid>
                </Grid>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEmploymentStatusDialog}>Cancel</Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmitEmploymentStatus}
            >
              Update Status
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={5000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

// TabPanel component for the tabs
const TabPanel = (props) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

export default EmployeeView;
